import { Injectable, signal, computed } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
    AI_ASSISTANT_DEFAULT_SUGGESTIONS,
    AI_ASSISTANT_FALLBACK,
    AI_ASSISTANT_MAX_SUGGESTIONS,
    AI_ASSISTANT_ROUTE_SUGGESTIONS,
    AI_ASSISTANT_TEASER_KEY,
    AI_ASSISTANT_WELCOME,
} from './ai-assistant.constants';
import { matchKnowledge } from './ai-assistant-knowledge.util';
import {
    getRelatedSuggestions,
    getWelcomeRelatedSuggestions,
} from './ai-assistant-related.util';
import { AiAssistantMessage, AiAssistantSuggestion } from './ai-assistant.types';

function createId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

@Injectable({ providedIn: 'root' })
export class AiAssistantService {
    readonly panelOpen = signal(false);
    readonly minimized = signal(false);
    readonly isTyping = signal(false);
    readonly messages = signal<AiAssistantMessage[]>([]);
    readonly currentUrl = signal('');

    readonly teaserDismissed = signal(this.readTeaserDismissed());

    readonly suggestions = computed(() => this.resolveSuggestions(this.currentUrl()));

    readonly showTeaser = computed(
        () => !this.teaserDismissed() && !this.panelOpen() && !this.minimized(),
    );

    constructor(private readonly router: Router) {
        this.router.events
            .pipe(filter((e) => e instanceof NavigationEnd))
            .subscribe((e) => {
                const url = (e as NavigationEnd).urlAfterRedirects || (e as NavigationEnd).url;
                this.currentUrl.set(url);
            });
        this.currentUrl.set(this.router.url);
        this.resetConversation();
    }

    openPanel(): void {
        this.panelOpen.set(true);
        this.minimized.set(false);
    }

    closePanel(): void {
        this.panelOpen.set(false);
        this.minimized.set(false);
    }

    minimizePanel(): void {
        this.panelOpen.set(false);
        this.minimized.set(true);
    }

    togglePanel(): void {
        if (this.panelOpen()) {
            this.closePanel();
        } else {
            this.openPanel();
        }
    }

    dismissTeaser(): void {
        this.teaserDismissed.set(true);
        try {
            localStorage.setItem(AI_ASSISTANT_TEASER_KEY, '1');
        } catch {
            /* ignore */
        }
    }

    resetConversation(): void {
        this.messages.set([
            {
                id: createId(),
                role: 'assistant',
                text: AI_ASSISTANT_WELCOME,
                createdAt: Date.now(),
                related: getWelcomeRelatedSuggestions(),
            },
        ]);
    }

    sendUserMessage(text: string): void {
        const trimmed = text?.trim();
        if (!trimmed || this.isTyping()) {
            return;
        }

        this.messages.update((list) => [
            ...list,
            { id: createId(), role: 'user', text: trimmed, createdAt: Date.now() },
        ]);

        this.isTyping.set(true);
        const { answer, entryId } = matchKnowledge(trimmed);
        const reply = answer ?? AI_ASSISTANT_FALLBACK;
        const related = getRelatedSuggestions(entryId, trimmed);

        window.setTimeout(() => {
            this.messages.update((list) => [
                ...list,
                {
                    id: createId(),
                    role: 'assistant',
                    text: reply,
                    createdAt: Date.now(),
                    related: related.length ? related : undefined,
                },
            ]);
            this.isTyping.set(false);
        }, 500);
    }

    sendSuggestion(suggestion: AiAssistantSuggestion): void {
        this.sendUserMessage(suggestion.prompt);
    }

    private resolveSuggestions(url: string): AiAssistantSuggestion[] {
        const routeEntry = AI_ASSISTANT_ROUTE_SUGGESTIONS.find((entry) => entry.match.test(url));
        const route = routeEntry?.suggestions ?? [];
        const merged: AiAssistantSuggestion[] = [];
        const seen = new Set<string>();

        for (const chip of route) {
            if (!seen.has(chip.id)) {
                merged.push(chip);
                seen.add(chip.id);
            }
        }

        for (const chip of AI_ASSISTANT_DEFAULT_SUGGESTIONS) {
            if (merged.length >= AI_ASSISTANT_MAX_SUGGESTIONS) {
                break;
            }
            if (!seen.has(chip.id)) {
                merged.push(chip);
                seen.add(chip.id);
            }
        }

        return merged.length ? merged : AI_ASSISTANT_DEFAULT_SUGGESTIONS.slice(0, AI_ASSISTANT_MAX_SUGGESTIONS);
    }

    private readTeaserDismissed(): boolean {
        try {
            return localStorage.getItem(AI_ASSISTANT_TEASER_KEY) === '1';
        } catch {
            return false;
        }
    }
}
